import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SearchIcon, File as FileIcon } from 'lucide-react';
import { format } from 'date-fns';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { useSearchFiles } from '@/api/files';
import { useOrganizations } from '@/api/organizations';
import { useUsers } from '@/api/users';
import { usePageTitle } from '@/hooks/usePageTitle';

const PAGE_LIMIT = 50;

export function SearchPage() {
  usePageTitle('Search');
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQ = searchParams.get('q') ?? '';
  const [input, setInput] = useState(urlQ);
  const [debouncedQ, setDebouncedQ] = useState(urlQ);
  const [orgId, setOrgId] = useState('');
  const [uploaderId, setUploaderId] = useState('');
  const [offset, setOffset] = useState(0);

  const orgs = useOrganizations();
  // Scope user list to selected org when picked — otherwise show all users
  // visible to the actor so staff can pick uploaders across orgs they manage.
  const usersQuery = useUsers({
    organization_id: orgId || undefined,
    limit: 500,
    offset: 0,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQ(input);
      if (input) {
        setSearchParams({ q: input }, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [input, setSearchParams]);

  // Reset to page 1 when the filters change.
  useEffect(() => {
    setOffset(0);
  }, [debouncedQ, orgId, uploaderId]);

  // Clear uploader when org changes (old uploader may no longer be listed).
  useEffect(() => {
    setUploaderId('');
  }, [orgId]);

  const { data, isLoading } = useSearchFiles({
    q: debouncedQ || undefined,
    org_id: orgId || undefined,
    uploaded_by_id: uploaderId || undefined,
    limit: PAGE_LIMIT,
    offset,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasAnyFilter = !!debouncedQ || !!orgId || !!uploaderId;

  const clearFilters = () => {
    setInput('');
    setDebouncedQ('');
    setOrgId('');
    setUploaderId('');
    setSearchParams({}, { replace: true });
  };

  return (
    <AppShell crumbs={[{ label: 'Search' }]}>
      <div className="space-y-4">
        <Card>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Search across all files in workspaces you can access. Use the filters to narrow by organization, uploader, or date.
            </p>
            <div className="relative">
              <SearchIcon
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Search files by name..."
                className="pl-9"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="search-org">Organization</Label>
                <Select
                  id="search-org"
                  value={orgId}
                  onChange={setOrgId}
                  options={(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name }))}
                  placeholder="All organizations"
                />
              </div>
              <div>
                <Label htmlFor="search-uploader">Uploader</Label>
                <Select
                  id="search-uploader"
                  value={uploaderId}
                  onChange={setUploaderId}
                  disabled={usersQuery.isLoading}
                  options={(usersQuery.data?.items ?? []).map((u) => ({
                    value: u.id,
                    label: u.full_name || u.email,
                  }))}
                  placeholder="Any uploader"
                />
              </div>
            </div>
          </div>
        </Card>

        <Card title="Results" padded={false}>
          <div className="p-4">
            {!hasAnyFilter ? (
              <EmptyState
                title="Search for files"
                description="Type a filename to search across your accessible workspaces."
                icon={<SearchIcon size={24} />}
              />
            ) : isLoading ? (
              <div className="flex justify-center py-10">
                <Spinner size={28} />
              </div>
            ) : items.length === 0 ? (
              <EmptyState
                title="No results"
                description="No files match your filters. Try a different search or clear filters."
                icon={<SearchIcon size={24} />}
                action={
                  <Button variant="secondary" onClick={clearFilters}>
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <>
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                        <th className="py-2 px-3 font-semibold">File name</th>
                        <th className="py-2 px-3 font-semibold">Folder</th>
                        <th className="py-2 px-3 font-semibold">Date</th>
                        <th className="py-2 px-3 font-semibold text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((f) => (
                        <tr key={f.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 px-3">
                            <Link
                              to={`/files/${f.id}`}
                              className="inline-flex items-center gap-2 text-slate-800 hover:text-bb-navy truncate"
                            >
                              <FileIcon size={16} className="text-bb-lilac" />
                              <span className="truncate">{f.name}</span>
                            </Link>
                          </td>
                          <td className="py-2 px-3 text-sm text-slate-600 whitespace-nowrap">
                            {f.folder_name ?? '—'}
                          </td>
                          <td className="py-2 px-3 text-sm text-slate-600 whitespace-nowrap">
                            {f.created_at ? format(new Date(f.created_at), 'PP') : '—'}
                          </td>
                          <td className="py-2 px-3 text-right">
                            <Link
                              to={`/files/${f.id}`}
                              className="text-xs text-bb-navy hover:underline"
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  total={total}
                  limit={PAGE_LIMIT}
                  offset={offset}
                  onChange={setOffset}
                  className="mt-3 border-t border-slate-100 pt-2"
                />
              </>
            )}
          </div>
        </Card>
      </div>
    </AppShell>
  );
}
